import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ActKc2TemplateInput } from 'gql/graphql';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { downloadURI } from 'utils/downloadDataURI';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { exportActKC2TemplateAsExcelGql } from '../exportActKC2TemplateQueries';

const asyncDownloadURI = async (dataUrl: string, name: string) => {
    await downloadURI(dataUrl, name);
};

export const useActKC2TemplateDownload = (workOrderId: number, onClose: () => void) => {
    const dispatch = useDispatch();

    const [isSubmittedDownload, setIsSubmittedDownload] = useState<boolean>(false);

    const [
        exportActKC2TemplateAsExcelData,
        exportActKC2TemplateAsExcelStatus,
        exportActKC2TemplateAsExcelError,
        exportActKC2TemplateAsExcelRequest,
        exportActKC2TemplateAsExcelSetData,
        exportActKC2TemplateAsExcelAbort,
    ] = useRequest({
        requestId: `exportActKC2TemplateAsExcel`,
        query: exportActKC2TemplateAsExcelGql,
        withEffect: false,
    });

    useEffect(() => {
        if (
            isSubmittedDownload &&
            exportActKC2TemplateAsExcelStatus === RequestStatus.SUCCESS &&
            exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel?.result
        ) {
            const { dataUrl, name } = exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel.data || {};

            if (dataUrl && name) {
                asyncDownloadURI(dataUrl, name);
                dispatch(exportActKC2TemplateAsExcelSetData(undefined));
                setIsSubmittedDownload(false);
                onClose();
            }
        }
    }, [
        dispatch,
        exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel.data,
        exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel?.result,
        exportActKC2TemplateAsExcelSetData,
        exportActKC2TemplateAsExcelStatus,
        isSubmittedDownload,
        onClose,
    ]);

    const onDownloadActKc2Template = useCallback(
        (input: ActKc2TemplateInput) => {
            dispatch(exportActKC2TemplateAsExcelRequest({ workOrderId, input }));
            setIsSubmittedDownload(true);
        },
        [dispatch, exportActKC2TemplateAsExcelRequest, workOrderId],
    );

    const onCloseModalErrorDownload = useCallback(() => {
        setIsSubmittedDownload(false);
    }, []);

    const onCreateReportDownload = useCallback(() => {
        ExportCSVReport(exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel?.validation);
    }, [exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel?.validation]);

    const onActKC2AsExcelAbort = useCallback(() => {
        dispatch(exportActKC2TemplateAsExcelAbort());
        setIsSubmittedDownload(false);
    }, [dispatch, exportActKC2TemplateAsExcelAbort]);

    return {
        isSubmittedDownload,
        exportActKC2TemplateAsExcelData,
        exportActKC2TemplateAsExcelStatus,
        exportActKC2TemplateAsExcelError,
        onActKC2AsExcelAbort,
        onDownloadActKc2Template,
        onCloseModalErrorDownload,
        onCreateReportDownload,
    };
};
